import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined'
import PolylineOutlinedIcon from '@mui/icons-material/PolylineOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../firebase'

const getTimestampValue = (value) => {
  if (!value) {
    return 0
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().getTime()
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  return 0
}

const formatTimestamp = (value, language) => {
  const timestamp = typeof value?.toDate === 'function' ? value.toDate() : value

  if (!(timestamp instanceof Date) || Number.isNaN(timestamp.getTime())) {
    return '-'
  }

  return new Intl.DateTimeFormat(language || 'tr-TR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(timestamp)
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { currentUser, userRole } = useAuth()

  const [definitions, setDefinitions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!currentUser?.uid) {
      setDefinitions([])
      setIsLoading(false)
      return undefined
    }

    setIsLoading(true)
    setLoadError('')

    const definitionsQuery = query(
      collection(db, 'aoiDefinitions'),
      where('ownerId', '==', currentUser.uid)
    )

    const unsubscribe = onSnapshot(
      definitionsQuery,
      (snapshot) => {
        const nextDefinitions = snapshot.docs
          .map((documentSnapshot) => ({
            id: documentSnapshot.id,
            ...documentSnapshot.data(),
          }))
          .sort((left, right) => {
            const rightValue = getTimestampValue(right.updatedAt || right.createdAt)
            const leftValue = getTimestampValue(left.updatedAt || left.createdAt)

            return rightValue - leftValue
          })

        setDefinitions(nextDefinitions)
        setIsLoading(false)
      },
      (error) => {
        console.error('AOI tanimlari alinirken hata:', error)
        setLoadError(
          t('dashboardPage.loadError', {
            defaultValue: 'Kayitli AOI tanimlari yuklenemedi.',
          })
        )
        setIsLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [currentUser?.uid, t])

  const totalSavedAreas = useMemo(
    () =>
      definitions.reduce((total, definition) => total + Number(definition.totalAreaCount || 0), 0),
    [definitions]
  )

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 2, md: 4 },
        py: 4,
        background: 'linear-gradient(180deg, #f7f9fc 0%, #eef4f8 100%)',
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
            {t('dashboardPage.title', { defaultValue: 'Dashboard' })}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('dashboardPage.subtitle', {
              defaultValue:
                'AOI tanimlari icin yeni bir calisma baslatin ve gorseller uzerinde alanlari dogrudan isaretleyin.',
            })}
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Card
            sx={{
              flex: 1,
              borderRadius: 4,
              background: 'linear-gradient(135deg, #ffffff 0%, #eef6ff 100%)',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={2}>
                <Chip
                  icon={<GridViewOutlinedIcon />}
                  label={t('dashboardPage.featureLabel', { defaultValue: 'AOI Module' })}
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t('dashboardPage.aoiCardTitle', {
                    defaultValue: 'Yeni AOI tanimi ekleyin',
                  })}
                </Typography>
                <Typography color="text.secondary">
                  {t('dashboardPage.aoiCardBody', {
                    defaultValue:
                      'Bir gorsel yukleyin, fare ile alanlari cizin, sonra her alan icin isim, renk ve aciklama tanimlayin.',
                  })}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddBoxOutlinedIcon />}
                  onClick={() => navigate('/aoi/new')}
                  sx={{ alignSelf: 'flex-start', mt: 1 }}
                >
                  {t('dashboardPage.newAoiButton', {
                    defaultValue: 'Yeni AOI tanimi ekle',
                  })}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card
            variant="outlined"
            sx={{
              width: { xs: '100%', md: 320 },
              borderRadius: 4,
              boxShadow: 'none',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('dashboardPage.savedSummaryLabel', {
                    defaultValue: 'Kayitli ozet',
                  })}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {definitions.length}
                </Typography>
                <Typography color="text.secondary">
                  {t('dashboardPage.savedDefinitionsLabel', {
                    defaultValue: 'kayitli AOI tanimi',
                  })}
                </Typography>
                <Chip
                  icon={<PhotoLibraryOutlinedIcon />}
                  label={t('dashboardPage.savedImageCount', {
                    defaultValue: '{{count}} gorsel',
                    count: definitions.reduce(
                      (total, definition) => total + Number(definition.imageCount || 0),
                      0
                    ),
                  })}
                  variant="outlined"
                />
                <Chip
                  icon={<PolylineOutlinedIcon />}
                  label={t('dashboardPage.savedAreaCount', {
                    defaultValue: '{{count}} AOI alani',
                    count: totalSavedAreas,
                  })}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f3f0ff 100%)',
            boxShadow: '0 20px 50px rgba(76, 29, 149, 0.08)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <Box>
                <Chip
                  icon={<QuizOutlinedIcon />}
                  label={t('dashboardPage.fusionLabel', { defaultValue: 'FusionLab' })}
                  color="secondary"
                  variant="outlined"
                  sx={{ mb: 1.5 }}
                />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t('dashboardPage.fusionTitle', { defaultValue: 'Creon Fusion anketleri' })}
                </Typography>
                <Typography color="text.secondary">
                  {t('dashboardPage.fusionBody', {
                    defaultValue: 'Kazakistan ve Ukrayna consumer/doctor anketlerini yönetin ve tüm sonuçları tek raporda görüntüleyin.',
                  })}
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<QuizOutlinedIcon />}
                onClick={() => navigate('/fusion-con-ka')}
                sx={{ flexShrink: 0 }}
              >
                {t('dashboardPage.openFusionButton', { defaultValue: 'Anketi aç' })}
              </Button>
              {userRole === 'admin' && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={() => navigate('/reports/survey?survey_code=all')}
                  sx={{ flexShrink: 0 }}
                >
                  {t('dashboardPage.allSurveyReportsButton', {
                    defaultValue: 'Tüm anket raporları',
                  })}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            borderRadius: 4,
            boxShadow: 'none',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t('dashboardPage.savedDefinitionsTitle', {
                    defaultValue: 'Kayitli AOI tanimlari',
                  })}
                </Typography>
                <Typography color="text.secondary">
                  {t('dashboardPage.savedDefinitionsBody', {
                    defaultValue:
                      'Daha once kaydettiginiz AOI tanimlarini acin ve istediginiz yerden duzenlemeye devam edin.',
                  })}
                </Typography>
              </Box>

              {loadError ? <Alert severity="error">{loadError}</Alert> : null}

              {isLoading ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              ) : definitions.length ? (
                <Stack spacing={2}>
                  {definitions.map((definition) => (
                    <Card
                      key={definition.id}
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch">
                        {definition.coverImageUrl ? (
                          <Box
                            component="img"
                            src={definition.coverImageUrl}
                            alt={
                              definition.title ||
                              t('dashboardPage.aoiPreviewAlt', { defaultValue: 'AOI önizlemesi' })
                            }
                            sx={{
                              width: { xs: '100%', md: 220 },
                              height: { xs: 160, md: 'auto' },
                              objectFit: 'cover',
                              backgroundColor: '#e2e8f0',
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: { xs: '100%', md: 220 },
                              minHeight: 160,
                              display: 'grid',
                              placeItems: 'center',
                              background:
                                'linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(15, 23, 42, 0.06))',
                              color: '#475569',
                              flexShrink: 0,
                            }}
                          >
                            <GridViewOutlinedIcon fontSize="large" />
                          </Box>
                        )}

                        <CardContent sx={{ flex: 1, p: 3 }}>
                          <Stack spacing={2}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1.5}
                              justifyContent="space-between"
                              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                            >
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                  {definition.title ||
                                    t('dashboardPage.untitledDefinition', {
                                      defaultValue: 'Adsiz AOI tanimi',
                                    })}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', useFlexGap: true }}>
                                  <Chip
                                    size="small"
                                    icon={<PhotoLibraryOutlinedIcon />}
                                    label={t('dashboardPage.definitionImages', {
                                      defaultValue: '{{count}} gorsel',
                                      count: Number(definition.imageCount || 0),
                                    })}
                                  />
                                  <Chip
                                    size="small"
                                    icon={<PolylineOutlinedIcon />}
                                    label={t('dashboardPage.definitionAreas', {
                                      defaultValue: '{{count}} AOI',
                                      count: Number(definition.totalAreaCount || 0),
                                    })}
                                  />
                                </Stack>
                              </Box>

                              <Button
                                variant="contained"
                                startIcon={<FolderOpenOutlinedIcon />}
                                onClick={() => navigate(`/aoi/${definition.id}`)}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                              >
                                {t('dashboardPage.openDefinitionButton', {
                                  defaultValue: 'Kaydi ac',
                                })}
                              </Button>
                            </Stack>

                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                              <Chip
                                size="small"
                                icon={<ScheduleOutlinedIcon />}
                                variant="outlined"
                                label={t('dashboardPage.lastUpdated', {
                                  defaultValue: 'Son guncelleme: {{value}}',
                                  value: formatTimestamp(
                                    definition.updatedAt || definition.createdAt,
                                    i18n.language
                                  ),
                                })}
                              />
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info">
                  {t('dashboardPage.emptyDefinitions', {
                    defaultValue:
                      'Henuz kayitli bir AOI taniminiz yok. Yeni bir tanim olusturup kaydettiginizde burada listelenecek.',
                  })}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  )
}
